import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    startWith,
    pairwise,
    mergeMap,
    fromEvent,
    map,
    filter,
    timer,
} from 'rxjs';
import { CHAT_STORE, WORKITEMS_STORE } from '@dxp/core/stores';
import { IWorkItem } from '@dxp/core/models';
import { DesktopNotifierBaseService } from '../desktop-notifier-base.service';

@Injectable({
    providedIn: 'root',
})
export class WorkitemOfferedNotifierService extends DesktopNotifierBaseService {
    readonly #destroyRef = inject(DestroyRef);
    readonly #workItemsStore = inject(WORKITEMS_STORE);
    readonly #chatStore = inject(CHAT_STORE);

    readonly #activeNotificationIds = new Set<string>();

    readonly visibilityBecameVisible$ = fromEvent(
        document,
        'visibilitychange',
    ).pipe(
        map(() => document.visibilityState),
        startWith(document.visibilityState),
        pairwise(),
        filter(([prev, curr]) => prev === 'hidden' && curr === 'visible'),
    );

    readonly workItemOfferEvents$ = this.#workItemsStore.onWorkItemAdded$.pipe(
        mergeMap(workItem =>
            timer(2000).pipe(
                map(() => {
                    const id = workItem.workItemId;
                    return this.#workItemsStore
                        .workItems()
                        .find(w => w.workItemId === id);
                }),
                filter((w): w is IWorkItem => !!w),
                filter(
                    w =>
                        w.workItemState === 'Preview' ||
                        w.workItemState === 'Connected',
                ),
            ),
        ),
    );

    constructor() {
        super();

        this.#workItemsStore.onWorkItemRemoved$
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(workitem => {
                this.closeNotification(workitem.workItemId);
                this.#activeNotificationIds.delete(workitem.workItemId);
            });

        this.workItemOfferEvents$
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(workItem => {
                this.newTaskOffered(
                    workItem,
                    workItem.workItemState === 'Connected',
                );
            });

        this.visibilityBecameVisible$
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe(() => {
                for (const id of this.#activeNotificationIds) {
                    this.closeNotification(id);
                }
                this.#activeNotificationIds.clear();
            });
    }

    newTaskOffered(workItem: IWorkItem, isConnected: boolean) {
        let media = workItem.primaryMediaType.toString();

        switch (workItem.primaryMediaType) {
            case 'Webchat':
            case 'Messaging': {
                media = 'Chat';
                break;
            }
        }

        const chat = this.#chatStore.getConversation(
            workItem.primaryConversationId,
        );

        if (chat() && chat()?.information?.transferInitiatedBy) {
            this.notify({
                id: workItem.workItemId,
                body: `${chat()?.information?.transferInitiatedBy?.agentName} ${isConnected ? 'has' : 'wants to'} transfer a ${media.toLowerCase()} task to you.`,
                data: workItem,
            });
        } else {
            this.notify({
                id: workItem.workItemId,
                body: `You have been ${isConnected ? 'assigned' : 'offered'} a new ${media.toLowerCase()} task.`,
                data: workItem,
            });
        }

        this.#activeNotificationIds.add(workItem.workItemId);
    }
}
